// wai-cart-guard.js — theme-native cart restriction (flag:cool mixing + JPY cap)
// Pure logic is exported for unit testing; the browser controller self-initializes
// only when `window` exists (so node --test can import the pure functions safely).

export const REASON = { MIX: 'mix', CAP: 'cap' };

/**
 * Mix rule (Rule 1): a cool item cannot share a cart with any non-cool item.
 * Enforced at add time by vetoing the add-to-cart submit.
 * @param {{hasCool:boolean, hasNonCool:boolean, totalCents:number}} cart
 * @param {{cool:boolean}} incoming
 * @returns {{allowed:boolean, reason:(string|null)}}
 */
export function evaluateAdd(cart, incoming) {
  if (incoming.cool && cart.hasNonCool) return { allowed: false, reason: REASON.MIX };
  if (!incoming.cool && cart.hasCool) return { allowed: false, reason: REASON.MIX };
  return { allowed: true, reason: null };
}

export const DEFAULT_CAP_CENTS = 8500; // overridable via snippet config

/**
 * Cap rule (Rule 2): when the cart contains any cool item, its total must be <= cap.
 * @param {{hasCool:boolean, totalCents:number}} cart
 * @param {number} capCents
 * @returns {boolean} true if the cart violates the cap
 */
export function capViolated(cart, capCents) {
  return cart.hasCool && cart.totalCents > capCents;
}

/**
 * Derive the compact CartState used by the rules.
 * @param {{total_price:number, items:Array<{key:string}>}} cartJson  /cart.js payload
 * @param {Set<string>} coolKeys  line keys known to be cool (from DOM data-wai-cool)
 * @returns {{hasCool:boolean, hasNonCool:boolean, totalCents:number}}
 */
export function buildCartState(cartJson, coolKeys) {
  let hasCool = false;
  let hasNonCool = false;
  for (const item of cartJson.items) {
    if (coolKeys.has(item.key)) hasCool = true;
    else hasNonCool = true;
  }
  return { hasCool, hasNonCool, totalCents: cartJson.total_price };
}

/* ----------------------------- Browser controller ----------------------------- */

const TOAST_MS = 6000;

// The cart drawer is a modal <dialog> (showModal), painted in the browser "top layer"
// above all normal-DOM stacking contexts, so plain z-index can never sit in front of
// it (and ::backdrop dims everything below). The Popover API also renders in the top
// layer, so a popover toast can paint above the drawer — but only when it is a DOM
// descendant of that dialog (verified on Chrome 148: a body-level popover loses to the
// modal backdrop). Detect support once; unsupported browsers fall back to fixed+z-index.
const SUPPORTS_POPOVER =
  typeof HTMLElement !== 'undefined' &&
  HTMLElement.prototype &&
  typeof HTMLElement.prototype.showPopover === 'function' &&
  'popover' in HTMLElement.prototype;

// The topmost open modal dialog the toast must live under to paint above its backdrop,
// or <body> when none is open (e.g. the PDP mix block, where no drawer opens).
function topLayerHost() {
  const dialogs = document.querySelectorAll('dialog');
  for (let i = dialogs.length - 1; i >= 0; i--) {
    const d = dialogs[i];
    if (d.open && d.matches(':modal')) return d;
  }
  return document.body;
}

function ensureToast() {
  let toast = document.querySelector('.wai-cart-guard-toast');
  if (toast) return toast;
  toast = document.createElement('div');
  toast.className = 'wai-cart-guard-toast';
  toast.setAttribute('role', 'alert');
  toast.setAttribute('aria-live', 'assertive');
  // "manual" so the toast does not light-dismiss on outside click / Escape — we own
  // its lifetime via the auto-dismiss timer (transient toasts) and the ✕ button.
  if (SUPPORTS_POPOVER) toast.setAttribute('popover', 'manual');
  const text = document.createElement('span');
  text.className = 'wai-cart-guard-toast__text';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'wai-cart-guard-toast__close';
  close.setAttribute('aria-label', 'Close');
  close.textContent = '✕';
  // The ✕ dismisses the current notice; for the persistent cap toast we also mark it
  // dismissed so the gate does not immediately re-show it (until the cart is valid
  // again or checkout is re-attempted).
  close.addEventListener('click', () => {
    toast._dismissed = true;
    hideToast(toast);
  });
  toast.append(text, close);
  return toast;
}

// Host the toast in the current top-layer owner and fade it in.
function presentToast(toast) {
  const host = topLayerHost();
  if (toast.parentNode !== host) {
    if (SUPPORTS_POPOVER && toast.matches(':popover-open')) {
      try {
        toast.hidePopover();
      } catch (_e) {
        /* already closed */
      }
    }
    host.appendChild(toast);
  }
  if (SUPPORTS_POPOVER && toast.isConnected && !toast.matches(':popover-open')) {
    try {
      toast.showPopover();
    } catch (_e) {
      /* already open or detached */
    }
  }
  requestAnimationFrame(() => toast.classList.add('is-visible'));
}

// `persist` toasts stay until the underlying condition clears (or the shopper closes
// them) — a cap violation is a standing state, not a momentary notice. Transient
// toasts (e.g. the mix block, where there is nothing in the cart to fix) auto-dismiss.
function showToast(message, reason, persist) {
  if (!message) return;
  const toast = ensureToast();
  const textEl = toast.querySelector('.wai-cart-guard-toast__text');
  if (textEl) textEl.textContent = message;
  toast._reason = reason || null;
  toast._persist = !!persist;
  toast._dismissed = false; // an explicit show re-arms the notice
  presentToast(toast);
  clearTimeout(toast._hideTimer);
  if (persist) {
    toast._deadline = Infinity;
  } else {
    toast._deadline = Date.now() + TOAST_MS;
    toast._hideTimer = setTimeout(() => hideToast(toast), TOAST_MS);
  }
}

// Re-show the toast if a cart re-render dismissed its popover while it should still be
// visible. Preserves the original deadline/timer so a transient toast still fades.
function reassertToast() {
  const toast = document.querySelector('.wai-cart-guard-toast');
  if (!toast || !toast._deadline || Date.now() >= toast._deadline) return;
  presentToast(toast);
}

function hideToast(toast) {
  if (!toast) return;
  toast._persist = false;
  toast._deadline = 0;
  toast.classList.remove('is-visible');
  clearTimeout(toast._hideTimer);
  // Let the fade-out finish before pulling the element out of the top layer.
  if (SUPPORTS_POPOVER && typeof toast.hidePopover === 'function') {
    clearTimeout(toast._popoverTimer);
    toast._popoverTimer = setTimeout(() => {
      if (toast.matches(':popover-open')) {
        try {
          toast.hidePopover();
        } catch (_e) {
          /* already hidden */
        }
      }
    }, 220);
  }
}

function readConfig() {
  const el = document.getElementById('wai-cart-guard-config');
  const fallback = { capCents: DEFAULT_CAP_CENTS, messages: { mix: '', cap: '' }, product: null, cart: null };
  if (!el) return fallback;
  try {
    return { ...fallback, ...JSON.parse(el.textContent || '{}') };
  } catch (_e) {
    return fallback;
  }
}

// Collect the set of cool line-keys from the rendered cart DOM.
function coolKeysFromDom() {
  const keys = new Set();
  for (const row of document.querySelectorAll('[data-key][data-wai-cool="true"]')) {
    const key = row.getAttribute('data-key');
    if (key) keys.add(key);
  }
  return keys;
}

async function fetchCart() {
  const base = window.Theme?.routes?.cart_url || '/cart';
  const res = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}

function init() {
  const config = readConfig();

  // Live cart snapshot, seeded synchronously from Liquid so the mix veto and the
  // checkout gate are correct before the first async /cart.js refresh resolves.
  let cart = { hasCool: false, hasNonCool: false, totalCents: 0 };
  if (config.cart && typeof config.cart.totalCents === 'number') {
    cart = {
      hasCool: !!config.cart.hasCool,
      hasNonCool: !!config.cart.hasNonCool,
      totalCents: config.cart.totalCents,
    };
  }
  // `violated` is the single source of truth the checkout gate reads synchronously.
  const state = { violated: capViolated(cart, config.capCents) };

  async function refreshCart() {
    try {
      const json = await fetchCart();
      cart = buildCartState(json, coolKeysFromDom());
    } catch (_e) {
      /* keep previous snapshot on failure */
    }
  }

  // Show / keep / hide the persistent cap notice. Driven from applyGate so it is a
  // pure function of the gate state — re-applied after every re-render, and never
  // left hidden by a stale "valid" read (the next gate pass with fresh data corrects
  // it). Honors the shopper's ✕ dismissal until the cart is valid again.
  function syncCapToast(blocked) {
    const toast = document.querySelector('.wai-cart-guard-toast');
    if (blocked) {
      if (toast && toast._dismissed) return;
      if (toast && toast._reason === REASON.CAP && toast.classList.contains('is-visible')) {
        reassertToast();
      } else {
        showToast(config.messages.cap, REASON.CAP, true);
      }
    } else if (toast && toast._reason === REASON.CAP) {
      toast._dismissed = false; // re-arm for the next violation
      hideToast(toast);
    }
  }

  // ---- Checkout gate ----
  // Cap enforcement is declarative, not corrective: we never silently change the cart.
  // Instead we disable/gray every checkout affordance while a cool cart exceeds the
  // cap, flag the offending quantities, and show the persistent cap notice. Re-applied
  // after every cart re-render (the theme morphs the DOM and drops these otherwise).
  function applyGate(blocked) {
    for (const btn of document.querySelectorAll('button[name="checkout"], input[name="checkout"]')) {
      btn.classList.toggle('wai-cart-guard-blocked', blocked);
      if (blocked) {
        // Disable only if it isn't already (the theme renders it disabled for an empty
        // cart). Mark what we disable so unblocking restores exactly that and never
        // clears a pre-existing disabled set by the theme.
        if (!btn.disabled) {
          btn.setAttribute('disabled', '');
          btn.setAttribute('aria-disabled', 'true');
          if (config.messages.cap) btn.setAttribute('title', config.messages.cap);
          btn.setAttribute('data-wai-cart-guard-disabled', '');
        }
      } else if (btn.hasAttribute('data-wai-cart-guard-disabled')) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('title');
        btn.removeAttribute('data-wai-cart-guard-disabled');
      }
    }
    // Accelerated/dynamic checkout (Shop Pay etc.) bypasses the cart; hide it while
    // blocked. Only unhide what we hid, so a merchant-hidden widget stays hidden.
    for (const el of document.querySelectorAll(
      '.additional-checkout-buttons, .dynamic-checkout__content, [data-shopify="dynamic-checkout-cart"], .accelerated-checkout'
    )) {
      if (blocked) {
        if (!el.hasAttribute('hidden')) {
          el.setAttribute('hidden', '');
          el.setAttribute('data-wai-cart-guard-hid', '');
        }
      } else if (el.hasAttribute('data-wai-cart-guard-hid')) {
        el.removeAttribute('hidden');
        el.removeAttribute('data-wai-cart-guard-hid');
      }
    }
    // Draw the eye to the offending quantities in the cart.
    for (const qty of document.querySelectorAll('.wai-cart-guard-qty-flag')) {
      qty.classList.remove('wai-cart-guard-qty-flag');
    }
    if (blocked) {
      for (const row of document.querySelectorAll('[data-key][data-wai-cool="true"]')) {
        const qty = row.querySelector('quantity-selector-component, .quantity-selector');
        if (qty) qty.classList.add('wai-cart-guard-qty-flag');
      }
    }
    syncCapToast(blocked);
  }

  async function evaluate() {
    await refreshCart();
    state.violated = capViolated(cart, config.capCents);
    applyGate(state.violated);
  }

  // ---- Mix-rule veto: window capture runs before the theme's document handler ----
  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

      const pf = form.closest('product-form-component');
      const incomingCool = pf?.getAttribute('data-wai-cool') === 'true';

      const verdict = evaluateAdd(cart, { cool: incomingCool });
      if (!verdict.allowed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showToast(config.messages.mix, REASON.MIX, false);
        return;
      }
      // Allowed add: the cap depends on the new cart total, and a PDP add may not emit
      // cart:update — re-evaluate a few times as the add settles so the gate + toast
      // appear even when the drawer never opens.
      if (incomingCool) {
        for (const ms of [400, 900, 1600]) setTimeout(evaluate, ms);
      }
    },
    { capture: true }
  );

  // ---- Bulletproof gate: block checkout intent regardless of async evaluate timing.
  // The disabled attribute is the visual gate; these capture handlers are the backstop
  // for the brief window after a re-render re-creates an un-disabled button.
  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest && event.target.closest('[name="checkout"]');
      if (!btn) return;
      if (state.violated) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showToast(config.messages.cap, REASON.CAP, true);
      }
    },
    { capture: true }
  );
  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (!submitter || submitter.getAttribute('name') !== 'checkout') return;
      if (state.violated) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showToast(config.messages.cap, REASON.CAP, true);
      }
    },
    { capture: true }
  );

  // ---- Keep the gate in sync with the live cart ----
  document.addEventListener('cart:update', () => {
    evaluate();
  });

  // Re-check when the cart drawer opens (cap may have changed while it was closed).
  for (const dlg of document.querySelectorAll('dialog')) {
    dlg.addEventListener('toggle', () => {
      if (dlg.open) evaluate();
    });
  }

  // The theme re-renders cart sections by morphing the DOM, dropping our gate classes
  // and dismissing the toast popover. Re-apply the current gate synchronously (no
  // flicker) on every mutation, and re-check the cart on a short debounce.
  let evalTimer = null;
  let rafPending = false;
  const observer = new MutationObserver(() => {
    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        applyGate(state.violated);
      });
    }
    clearTimeout(evalTimer);
    evalTimer = setTimeout(evaluate, 200);
  });
  for (const t of document.querySelectorAll(
    'cart-drawer-component, cart-items-component, #cart-form, #cart_form, .cart-form, .shopify-cart-form'
  )) {
    observer.observe(t, { childList: true, subtree: true });
  }

  // Seed: apply the gate for the initial cart state (e.g. landing on /cart over cap).
  applyGate(state.violated);
  evaluate();
}

// Guarded init — only in the browser, only once.
if (typeof window !== 'undefined' && !window.__waiCartGuardInit) {
  window.__waiCartGuardInit = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
